import { K } from '../kabinet/atoms';
import { склонение } from '../sklonenie';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * МЕТКИ ИДУЩИХ ОПЫТОВ — СВОЕЙ СТРОКОЙ, А НЕ В ОБЩЕМ РЯДУ СВОЙСТВ.
 *
 * Передача дизайнера 07.09.2026, раздел 2.
 *
 * ПОЧЕМУ ОТДЕЛЬНОЙ СТРОКОЙ. Вид кампании, группа и площадки — это
 * СВОЙСТВА: они не меняются сами и описывают то, что настроено. Тест —
 * СОБЫТИЕ: он идёт сейчас и кончится сам. Вперемешку пять капсул
 * читались как один набор, и метка теста терялась среди «Поиск — сезон»
 * и «рекомендации».
 *
 * ПОДПИСИ ПОЛНЫЕ. «Тест ставки» и «ABC-тест фото» — два разных опыта:
 * первый водит ставку по вилке, второй подменяет главное фото карточки.
 * Сокращённых форм («ABC-фото») в интерфейсе нет: по короткому слову
 * их путали.
 *
 * ПРОГРЕСС ПРИХОДИТ ГОТОВЫМ. Дословно: «Прогресс в подсказке считается
 * на сервере — интерфейс его не пересчитывает».
 * ═══════════════════════════════════════════════════════════════════════
 */

const КЛАСТЕРЫ = Object.freeze(['кластер', 'кластера', 'кластеров'] as const);

export interface ТестСтавки {
  /** Кластеров в тесте. Поля может не быть у метки прежней сборки. */
  clusters: number;
  step: number;
  total: number;
  till: string;
}

export interface ТестФото {
  photo: number;
  photos: number;
  lap: number;
  laps: number;
  till: string;
}

export function подсказкаСтавки(т: ТестСтавки): string {
  /* ПОЛЕЙ МОЖЕТ НЕ БЫТЬ ПООДИНОЧКЕ. Служба прежней сборки отдаёт метку
     без `clusters` (их завели 03.09), и `undefined > 0` — ложь, а не
     падение; но `склонение(undefined)` уже роняет. Приводим к нулю. */
  const кластеров = т.clusters ?? 0;
  const где =
    кластеров > 0 ? `${кластеров} ${склонение(кластеров, КЛАСТЕРЫ)}` : 'ставка товара';
  return `Идёт тест ставки: ${где} · круг ${т.step} из ${т.total} · до ${т.till}`;
}

export function подсказкаФото(т: ТестФото): string {
  return `Идёт ABC-тест фото: фото ${т.photo} из ${т.photos} · круг ${т.lap} из ${т.laps} · до ${т.till}`;
}

/** Столбики — значок теста ставки: он водит ставку по вилке. */
function ЗначокСтолбиков({ размер }: { размер: number }) {
  return (
    <svg
      width={размер}
      height={размер}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      style={{ flex: '0 0 auto' }}
      aria-hidden="true"
    >
      <path d="M2.6 13.4h10.8" strokeLinecap="round" />
      <path d="M5 11V7.4M8 11V3.6M11 11V5.8" strokeLinecap="round" />
    </svg>
  );
}

/** Кадр — значок ABC-теста: он подменяет фотографию. */
function ЗначокКадра({ размер }: { размер: number }) {
  return (
    <svg
      width={размер}
      height={размер}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      style={{ flex: '0 0 auto' }}
      aria-hidden="true"
    >
      <rect x="2.4" y="3.6" width="11.2" height="8.8" rx="1.6" />
      <circle cx="8" cy="8" r="2.2" />
    </svg>
  );
}

/**
 * Строка меток. Ничего не идёт — строки нет вовсе: пустой ряд занимал бы
 * место у каждой из сорока кампаний.
 */
export function МеткиТестов({
  ставка,
  фото,
  узкий = false,
}: {
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ПУСТОТУ ЧАСТИЦА РЕШАЕТ САМА — И ЭТО ВТОРОЕ ТАКОЕ ПАДЕНИЕ ЗА ВЕЧЕР.
   *
   * 08.09.2026 снимок списка кампаний показал вместо таблицы красную
   * плашку «Не удалось показать: кабинет» и строку
   * «Cannot read properties of undefined (reading 'clusters')».
   *
   * Условие стояло `ставка === null`, а поля в ответе могло не быть
   * СОВСЕМ. `undefined !== null` истинно — и подсказка читала у пустоты
   * число кластеров.
   *
   * ТОЧНО ТАК ЖЕ УПАЛ ЭКРАН КАМПАНИИ ЧАСОМ РАНЬШЕ (капсулы СПП
   * и остатка, `zapas.tsx`). Тогда я починил одно место и не пошёл
   * искать остальные — а надо было: правило «поля может не быть»
   * относится к КАЖДОЙ частице, читающей ответ службы, и одно
   * исправленное место означает лишь, что упадёт соседнее.
   *
   * ЗДЕСЬ ЦЕНА ВЫШЕ, ЧЕМ ТАМ: метки стоят в СПИСКЕ кампаний, то есть
   * на первом экране кабинета. Пустая метка стоила бы строки; пустота,
   * прочитанная как объект, стоит всего кабинета.
   * ═══════════════════════════════════════════════════════════════ */
  ставка: ТестСтавки | null | undefined;
  фото: ТестФото | null | undefined;
  /** На 390 капсула выше — 24 против 20, и значок крупнее. */
  узкий?: boolean;
}) {
  const ст = ставка ?? null;
  const фт = фото ?? null;
  if (ст === null && фт === null) return null;
  const значок = узкий ? 11 : 10;
  return (
    <span data-d="метки-тестов" className={K.меткиТестов}>
      {ст !== null && (
        <span
          data-d="метка-теста-ставки"
          title={подсказкаСтавки(ст)}
          className={K.меткаТестаСтавки}
        >
          <ЗначокСтолбиков размер={значок} />
          Тест ставки
        </span>
      )}
      {фт !== null && (
        <span data-d="метка-теста-фото" title={подсказкаФото(фт)} className={K.меткаТестаФото}>
          <ЗначокКадра размер={значок} />
          ABC-тест фото
        </span>
      )}
    </span>
  );
}
